import type { FunctionComponent } from 'react';
import { useState } from 'react';
import { CirclePlay, Image } from 'lucide-react';

import { ctw } from '@/common/utils/ctw';
import {
  Button,
  Card,
  CheckCircle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  LockCircle,
} from '@/components';
import { VideoPlayer } from '@/components/molecules';

type GatedContentCheckBarProps = {
  status: 'success' | 'failed';
  message?: string;
  videoUrl: string | null;
  screenshotUrl?: string | null;
  websiteUrl?: string;
  className?: string;
  isGatedContentCheckEnabled: boolean;
};

export const GatedContentCheckBar: FunctionComponent<GatedContentCheckBarProps> = ({
  status,
  message,
  videoUrl,
  screenshotUrl,
  websiteUrl,
  isGatedContentCheckEnabled,
  className,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const checkStatus = (() => {
    if (!isGatedContentCheckEnabled) {
      return 'Detected';
    }

    if (status === 'success') {
      return 'Successful';
    }

    if (status === 'failed') {
      return 'Failed';
    }
  })();
  const messageToDisplay = !isGatedContentCheckEnabled
    ? 'Activate "Gated Content Websites Scans" on report creation or by contacting sales.'
    : message;

  const hasVideo = Boolean(videoUrl);
  const hasScreenshot = Boolean(screenshotUrl);
  const hasEvidence = hasVideo || hasScreenshot;

  return (
    <Card className={ctw('mb-6 rounded-2xl bg-white px-5 py-2', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {status === 'success' && (
            <CheckCircle
              size={29}
              className={`stroke-success stroke-[0.1875rem]`}
              containerProps={{
                className: 'bg-success/20',
              }}
            />
          )}
          {(status === 'failed' || !isGatedContentCheckEnabled) && (
            <LockCircle
              size={29}
              className={`stroke-error stroke-[0.1875rem]`}
              containerProps={{
                className: 'bg-error/20',
              }}
            />
          )}
          <div className="flex items-center gap-6">
            <span className="text-sm font-semibold">Gated Content Check {checkStatus}</span>
            <span className="text-sm">{messageToDisplay}</span>
          </div>
        </div>
        {isGatedContentCheckEnabled && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={ctw('gap-1.5', {
                  'pointer-events-none opacity-50': !hasEvidence,
                })}
                disabled={!hasEvidence}
                title={!hasEvidence ? 'Evidence not available' : undefined}
              >
                {hasVideo ? <CirclePlay className="h-4 w-4" /> : <Image className="h-4 w-4" />}
                <span>Watch evidence</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[90vw] sm:max-w-7xl">
              <DialogHeader>
                <DialogTitle>Gated Content Evidence</DialogTitle>
              </DialogHeader>
              {hasVideo ? (
                <VideoPlayer videoUrl={videoUrl} websiteUrl={websiteUrl} />
              ) : (
                <div className="flex justify-center">
                  <img
                    src={screenshotUrl!}
                    alt="Gated content screenshot"
                    className="max-h-[80vh] w-auto rounded-lg object-contain"
                  />
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Card>
  );
};

GatedContentCheckBar.displayName = 'GatedContentCheckBar';
